/**
 * Generate a cryptographically secure token
 * Uses 32 bytes (256 bits) of entropy for maximum security
 * Uses Web Crypto API for browser compatibility
 */
export function generateToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Hash a token using SHA-256 for secure database storage
 * This matches the magic link system's approach
 * Uses Web Crypto API for browser compatibility
 */
export async function hashToken(token: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(token);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = new Uint8Array(hashBuffer);
  return btoa(String.fromCharCode(...hashArray))
    .replace(/\+/g, '-')   // URL-safe characters
    .replace(/\//g, '_')   // URL-safe characters
    .replace(/=+$/, '');   // Remove base64 padding
}

/**
 * Validate token format and basic requirements.
 *
 * Accepted formats:
 *  - NEW  base62 short token:  [A-Za-z0-9]{16}          (survey-mint v2)
 *  - OLD  base64url long token: [A-Za-z0-9_-]{32,}       (legacy magic-link)
 *  - PERMISSIVE fallback:       [A-Za-z0-9_-]{16,}       (anything that looks like a token)
 *
 * We intentionally do NOT reject on format alone — the backend is the
 * authoritative source of truth. Format validation here only catches
 * obviously malformed / missing tokens before wasting a network call.
 */
export function isValidTokenFormat(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  const trimmed = token.trim();

  // New base62 short token: exactly 16 alphanumeric chars
  const isBase62Short = /^[A-Za-z0-9]{16}$/.test(trimmed);

  // Legacy base64url long token: 32+ chars, may contain - and _
  const isBase64UrlLong = /^[A-Za-z0-9_-]{32,}$/.test(trimmed);

  // Permissive fallback: any token-like string of at least 16 chars
  const isPermissive = /^[A-Za-z0-9_-]{16,}$/.test(trimmed);

  const valid = isBase62Short || isBase64UrlLong || isPermissive;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug(
      `[tokenUtils] isValidTokenFormat → length=${trimmed.length}`,
      `base62Short=${isBase62Short}`,
      `base64UrlLong=${isBase64UrlLong}`,
      `permissive=${isPermissive}`,
      `valid=${valid}`
    );
  }

  return valid;
}

/**
 * Create session context after successful token validation
 */
export async function createSessionContext(tokenData: {
  token: string;
  hospital_id: string;
  patient_name?: string;
  link_id: string;
}) {
  const sessionContext = {
    hospital_id: tokenData.hospital_id,
    patient_name: tokenData.patient_name,
    link_id: tokenData.link_id,
    token_plain: tokenData.token, // Keep plain token for backend verification
    timestamp: new Date().toISOString(),
    validated: true
  };

  // Store in localStorage for persistence across browser sessions
  localStorage.setItem('feedback_session', JSON.stringify(sessionContext));
  
  // Store validation state in sessionStorage (cleared when tab closes)
  const hashedToken = await hashToken(tokenData.token);
  sessionStorage.setItem(`feedback_validated_${hashedToken}`, 'true');
  
  return sessionContext;
}

/**
 * Get current session context
 */
export function getSessionContext() {
  try {
    const stored = localStorage.getItem('feedback_session');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error parsing session context:', error);
    return null;
  }
}

/**
 * Clear session context
 */
export function clearSessionContext() {
  localStorage.removeItem('feedback_session');
  // Clear all validation states from sessionStorage
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith('feedback_validated_')) {
      sessionStorage.removeItem(key);
    }
  });
}

/**
 * Check if token was already validated in this session
 */
export async function isTokenAlreadyValidated(token: string): Promise<boolean> {
  const hashedToken = await hashToken(token);
  const key = `feedback_validated_${hashedToken}`;
  return sessionStorage.getItem(key) === 'true';
}